import React from 'react';
import { Box, BoxProps, SpacingStack } from '@withjoy/joykit';
import ExplodedCategory from '../ExplodedCategory';
import { useRecommendedCategoriesSection } from './RecommendedCategoriesSection.controller';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { RawCatalogCategory, RecommendedCategoriesSectionProps } from './RecommendedCategoriesSection.types';
import { withLazyPropOnIntersection } from '@shared/utils/hooks/useLazyLoadImage';

const SKELETON_ITEMS_AMOUNT = 2;

// To improve first time load, we don't want to render all of the exploded categories
let hasBeenShownOnce = false;

const Root = React.forwardRef<HTMLDivElement, RecommendedCategoriesSectionProps & { isOnScreen: boolean }>(({ eventId, isOnScreen }, ref) => {
  // After showing this component once, we can leverage the apollo cache
  if (!hasBeenShownOnce && isOnScreen) {
    hasBeenShownOnce = true;
  }

  const { catalogEntityCategories, loadingCategorySections, goToCategory } = useRecommendedCategoriesSection({ eventId, skip: !hasBeenShownOnce && !isOnScreen });

  return (
    <Box ref={ref} width="100%">
      <SkeletonGroup isReady={!loadingCategorySections || !!catalogEntityCategories.length} placeholder={Skeleton} width="100%">
        <Main>
          {catalogEntityCategories.map(({ catalogCategoryData }) => {
            const { id, title, publicUrlPath, subCategories, browseMoreBanner } = catalogCategoryData as NonNullable<RawCatalogCategory>;
            return (
              <ExplodedCategory
                key={id}
                id={id}
                title={title}
                publicUrlPath={publicUrlPath}
                subcategories={subCategories}
                browseCategoryBannerText={`Interested in more gifts from ${title}?`}
                onBrowseCategoryClick={goToCategory}
                browseMoreBanner={browseMoreBanner}
              />
            );
          })}
        </Main>
      </SkeletonGroup>
    </Box>
  );
});

const Main = React.forwardRef<HTMLDivElement, BoxProps>((props, ref) => <SpacingStack {...props} spacing={[9, null, '98px']} ref={ref} />);

const Skeleton = () => {
  <Main>
    {Array.from({ length: SKELETON_ITEMS_AMOUNT }).map((_, index) => (
      <ExplodedCategory.Skeleton key={index} id={`${index}`} subcategoryCount={3} />
    ))}
  </Main>;
};

const LazyLoadingCategoryRecommendationsCarousel = withLazyPropOnIntersection(Root, 'isOnScreen');

export const RecommendedCategoriesSection: React.FC<RecommendedCategoriesSectionProps> & {
  Skeleton: typeof Skeleton;
} = props => {
  return <LazyLoadingCategoryRecommendationsCarousel isOnScreen={{ onscreen: true, offscreen: false }} {...props} />;
};

RecommendedCategoriesSection.Skeleton = Skeleton;
